'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { X, Plus, FolderHeart, Check, Loader2, BookmarkPlus, ChevronRight } from 'lucide-react';
import { collectionsService, PropertyCollection, SavePropertyInput } from '@/lib/collections';
import { useAuth } from '@/contexts/AuthContext';

interface SaveToCollectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  property: SavePropertyInput;
}

const COLLECTION_COLORS = [
  '#ec4899', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4', '#84cc16',
];

const COLLECTION_ICONS = [
  { value: 'heart', label: '❤️' },
  { value: 'trending-up', label: '📈' },
  { value: 'map-pin', label: '📍' },
  { value: 'folder', label: '📁' },
  { value: 'star', label: '⭐' },
  { value: 'home', label: '🏠' },
];

export default function SaveToCollectionModal({ isOpen, onClose, property }: SaveToCollectionModalProps) {
  const { user } = useAuth();
  const [collections, setCollections] = useState<PropertyCollection[]>([]);
  const [savedCollectionIds, setSavedCollectionIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(COLLECTION_COLORS[0]);
  const [creating, setCreating] = useState(false);
  const [noteCollectionId, setNoteCollectionId] = useState<string | null>(null);
  const [noteText, setNoteText] = useState('');

  const loadData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const [cols, savedIds] = await Promise.all([
      collectionsService.getUserCollections(),
      collectionsService.getPropertyCollections(property.propertyId),
    ]);
    setCollections(cols);
    setSavedCollectionIds(savedIds);
    setLoading(false);
  }, [user, property.propertyId]);

  useEffect(() => {
    if (isOpen && user) {
      loadData();
    }
  }, [isOpen, user, loadData]);

  const handleToggleCollection = async (collectionId: string) => {
    const isSaved = savedCollectionIds.includes(collectionId);
    setSavingId(collectionId);

    if (isSaved) {
      const ok = await collectionsService.removePropertyFromCollection(collectionId, property.propertyId);
      if (ok) setSavedCollectionIds((prev) => prev.filter((id) => id !== collectionId));
    } else {
      const result = await collectionsService.savePropertyToCollection(collectionId, property);
      if (result) {
        setSavedCollectionIds((prev) => [...prev, collectionId]);
        setNoteCollectionId(collectionId);
      }
    }

    setSavingId(null);
  };

  const handleSaveNote = async () => {
    if (!noteCollectionId) return;
    await collectionsService.updatePropertyNote(noteCollectionId, property.propertyId, noteText);
    setNoteCollectionId(null);
    setNoteText('');
  };

  const handleCreateCollection = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    const col = await collectionsService.createCollection({ name: newName.trim(), color: newColor });
    if (col) {
      setCollections((prev) => [...prev, col]);
      setNewName('');
      setShowCreateForm(false);
    }
    setCreating(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden animate-slide-up sm:animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-primary-light flex items-center justify-center">
              <BookmarkPlus size={16} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-bold text-foreground">Koleksiyona Kaydet</h2>
              <p className="text-xs text-muted-foreground line-clamp-1">{property.propertyTitle}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-xl hover:bg-muted flex items-center justify-center text-muted-foreground transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 size={24} className="animate-spin text-primary" />
            </div>
          ) : (
            <div className="p-4 space-y-2">
              {collections.length === 0 && !showCreateForm && (
                <p className="text-center text-sm text-muted-foreground py-4">Henüz koleksiyon yok. İlk koleksiyonunuzu oluşturun.</p>
              )}

              {collections.map((col) => {
                const isSaved = savedCollectionIds.includes(col.id);
                const isLoading = savingId === col.id;
                return (
                  <div key={col.id}>
                    <button
                      onClick={() => handleToggleCollection(col.id)}
                      disabled={isLoading}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-150 ${
                        isSaved
                          ? 'border-primary/30 bg-primary-light' :'border-border hover:border-primary/20 hover:bg-muted/50'
                      }`}
                    >
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-sm font-bold"
                        style={{ backgroundColor: col.color }}
                      >
                        {col.name[0]?.toUpperCase()}
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <p className="text-sm font-semibold text-foreground truncate">{col.name}</p>
                        <p className="text-xs text-muted-foreground">{col.propertyCount} mülk</p>
                      </div>
                      <div className="flex-shrink-0">
                        {isLoading ? (
                          <Loader2 size={16} className="animate-spin text-primary" />
                        ) : isSaved ? (
                          <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                            <Check size={11} className="text-white" />
                          </div>
                        ) : (
                          <div className="w-5 h-5 rounded-full border-2 border-border" />
                        )}
                      </div>
                    </button>

                    {/* Note input after saving */}
                    {noteCollectionId === col.id && (
                      <div className="mt-2 ml-11 animate-fade-in">
                        <textarea
                          value={noteText}
                          onChange={(e) => setNoteText(e.target.value)}
                          placeholder="Bu mülk hakkında not ekle (isteğe bağlı)..."
                          className="w-full text-sm border border-border rounded-xl px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground"
                          rows={2}
                        />
                        <div className="flex gap-2 mt-1.5">
                          <button onClick={handleSaveNote} className="text-xs font-semibold text-primary hover:underline">
                            Notu Kaydet
                          </button>
                          <button onClick={() => { setNoteCollectionId(null); setNoteText(''); }} className="text-xs text-muted-foreground hover:underline">
                            Atla
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Create new collection form */}
              {showCreateForm ? (
                <div className="border border-dashed border-primary/40 rounded-xl p-4 space-y-3 animate-fade-in">
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Koleksiyon adı..."
                    className="w-full text-sm border border-border rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground"
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && handleCreateCollection()}
                  />
                  <div className="flex gap-2 flex-wrap">
                    {COLLECTION_COLORS.map((c) => (
                      <button
                        key={c}
                        onClick={() => setNewColor(c)}
                        className={`w-6 h-6 rounded-full transition-transform ${newColor === c ? 'scale-125 ring-2 ring-offset-1 ring-primary' : ''}`}
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleCreateCollection}
                      disabled={!newName.trim() || creating}
                      className="flex-1 btn-primary text-sm py-2 flex items-center justify-center gap-1.5 disabled:opacity-50"
                    >
                      {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                      Oluştur
                    </button>
                    <button onClick={() => setShowCreateForm(false)} className="px-4 py-2 text-sm border border-border rounded-xl hover:bg-muted transition-colors">
                      İptal
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-dashed border-border hover:border-primary/40 hover:bg-primary-light/50 transition-all duration-150 text-muted-foreground hover:text-primary"
                >
                  <div className="w-8 h-8 rounded-lg border-2 border-dashed border-current flex items-center justify-center">
                    <Plus size={14} />
                  </div>
                  <span className="text-sm font-semibold">Yeni Koleksiyon Oluştur</span>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-border flex items-center justify-between">
          <a href="/koleksiyonlar" className="flex items-center gap-1 text-sm text-primary font-semibold hover:underline">
            <FolderHeart size={14} />
            Koleksiyonlarım
            <ChevronRight size={12} />
          </a>
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors">
            Tamam
          </button>
        </div>
      </div>
    </div>
  );
}
